const LOCAL_DATE_TIME = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})(?::\d{2})?$/;
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Always show clocks as zero-padded 12-hour time (hh:mm AM/PM), independent of
 * device locale. Built manually so padding does not depend on the JS engine's
 * Intl implementation.
 */
function formatClock(date: Date): string {
  const hour = date.getHours();
  const twelveHour = hour % 12 || 12;
  const minute = date.getMinutes();
  const period = hour >= 12 ? 'PM' : 'AM';
  return `${String(twelveHour).padStart(2, '0')}:${String(minute).padStart(2, '0')} ${period}`;
}

function formatClockFromParts(hour: number, minute: number): string | null {
  if (
    !Number.isInteger(hour) ||
    !Number.isInteger(minute) ||
    hour < 0 ||
    hour > 23 ||
    minute < 0 ||
    minute > 59
  ) {
    return null;
  }
  return formatClock(new Date(2000, 0, 1, hour, minute));
}

/** Parse a YYYY-MM-DD string into a local calendar Date (noon local). */
export function parseCalendarDateInput(value: string): Date | null {
  const trimmed = value.trim();
  if (!DATE_ONLY.test(trimmed)) return null;
  const [yearText, monthText, dayText] = trimmed.split('-');
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    !Number.isInteger(day)
  ) {
    return null;
  }
  const date = new Date(year, month - 1, day, 12, 0, 0, 0);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }
  return date;
}

/** Format a Date as YYYY-MM-DD in the local calendar. */
export function toCalendarDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Whole calendar days from `from` to `to` (YYYY-MM-DD). */
export function calendarDaysBetween(from: string, to: string): number | null {
  const start = parseCalendarDateInput(from);
  const end = parseCalendarDateInput(to);
  if (!start || !end) return null;
  return Math.round((end.getTime() - start.getTime()) / 86400000);
}

/** Shift a YYYY-MM-DD calendar date by a whole number of days. */
export function addCalendarDays(value: string, days: number): string | null {
  const date = parseCalendarDateInput(value);
  if (!date) return null;
  date.setDate(date.getDate() + days);
  return toCalendarDateString(date);
}

/** Today's calendar date in the org business timezone (YYYY-MM-DD). */
export function todayBusinessCalendarDate(now = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: BUSINESS_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

/**
 * Business calendar timezone used by arkhrmsapi attendance marking
 * (`Asia/Kolkata`). API date-only business fields are often returned as UTC
 * midnights shifted to that zone (e.g. `2026-08-04T18:30:00.000Z` = 05 Aug IST).
 */
export const BUSINESS_TIME_ZONE = 'Asia/Kolkata';

/**
 * Normalize API calendar dates to YYYY-MM-DD in the org business timezone.
 * Plain `YYYY-MM-DD` values are returned unchanged.
 */
export function calendarDateFromApi(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  const text =
    typeof value === 'string'
      ? value.trim()
      : typeof value === 'number' && Number.isFinite(value)
        ? String(value)
        : null;
  if (!text) return null;
  if (DATE_ONLY.test(text)) return text;

  const parsed = new Date(text);
  if (!Number.isNaN(parsed.getTime())) {
    const formatted = new Intl.DateTimeFormat('en-CA', {
      timeZone: BUSINESS_TIME_ZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(parsed);
    if (DATE_ONLY.test(formatted)) return formatted;
  }

  const prefix = text.match(/^(\d{4}-\d{2}-\d{2})/)?.[1] ?? null;
  return prefix && DATE_ONLY.test(prefix) ? prefix : null;
}

export function formatDate(value: Date | string | null | undefined): string {
  const date = value instanceof Date ? value : value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return 'Date unavailable';
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

export function formatDateTime(value: Date | string | null | undefined): string {
  const date = value instanceof Date ? value : value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return 'Not available';
  const day = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
  return `${day}, ${formatClock(date)}`;
}

export function formatTime(value: string | null | undefined): string {
  if (!value) return 'Not available';
  const trimmed = value.trim();
  const timeOnly = trimmed.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (timeOnly) {
    const formatted = formatClockFromParts(
      Number(timeOnly[1]),
      Number(timeOnly[2]),
    );
    if (formatted) return formatted;
  }
  const localMatch = trimmed.match(LOCAL_DATE_TIME);
  if (localMatch) {
    const [hourText, minuteText] = localMatch[2].split(':');
    const formatted = formatClockFromParts(Number(hourText), Number(minuteText));
    if (formatted) return formatted;
  }
  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) return 'Not available';
  return formatClock(date);
}

/**
 * Human-readable duration for display and form inputs.
 * Examples: 30m, 45m, 01h 00m, 08h 30m, 09h 00m
 */
export function formatMinutes(value: number | null | undefined): string {
  const minutes = coerceNonNegativeMinutes(value);
  if (minutes === null) {
    if (
      typeof value === 'number' &&
      Number.isFinite(value) &&
      value < 0 &&
      typeof __DEV__ !== 'undefined' &&
      __DEV__
    ) {
      // Safe warning: no employee details.
      console.warn('Ignoring negative attendance duration from backend');
    }
    return 'Not available';
  }
  const hours = Math.floor(minutes / 60);
  const remainder = Math.round(minutes % 60);
  if (!hours) return `${remainder}m`;
  return `${String(hours).padStart(2, '0')}h ${String(remainder).padStart(2, '0')}m`;
}

/**
 * Parse duration text entered as Xm / Xh Ym (padded or unpadded).
 * Accepts: 30m, 01h 00m, 8h 30m, 1h. Rejects bare minute integers.
 */
export function parseDurationFormValue(
  value: string | null | undefined,
): number | null {
  if (!value?.trim()) return null;
  const trimmed = value.trim();
  const minutesOnly = trimmed.match(/^(\d+)\s*m(?:in(?:utes?)?)?$/i);
  if (minutesOnly) {
    return Number(minutesOnly[1]);
  }
  const hoursAndMinutes = trimmed.match(
    /^(\d+)\s*h(?:ours?)?\s+(\d+)\s*m(?:in(?:utes?)?)?$/i,
  );
  if (hoursAndMinutes) {
    const hours = Number(hoursAndMinutes[1]);
    const minutes = Number(hoursAndMinutes[2]);
    if (!Number.isInteger(hours) || !Number.isInteger(minutes) || minutes > 59) {
      return null;
    }
    return hours * 60 + minutes;
  }
  const hoursOnly = trimmed.match(/^(\d+)\s*h(?:ours?)?$/i);
  if (hoursOnly) {
    return Number(hoursOnly[1]) * 60;
  }
  return null;
}

export const DURATION_INPUT_HINT = 'e.g. 08h 30m or 30m';

export function durationFormatError(label: string): string {
  return `${label} must use format like 30m or 08h 30m.`;
}

function coerceNonNegativeMinutes(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') {
    if (!Number.isFinite(value) || value < 0) return null;
    return Math.round(value);
  }
  if (typeof value === 'string' && value.trim()) {
    const fromDuration = parseDurationFormValue(value);
    if (fromDuration !== null) return fromDuration;
    const asNum = Number(value.trim());
    if (Number.isFinite(asNum) && asNum >= 0) return Math.round(asNum);
  }
  return null;
}

/** Compact label for attendance durations (e.g. 90 → 01h 30m, 45 → 45m). */
export function formatDurationCompact(value: unknown): string {
  const minutes = coerceNonNegativeMinutes(value);
  if (minutes === null) return '—';
  return formatMinutes(minutes);
}

export const formatDurationMinutes = formatMinutes;

/**
 * Timestamp display priority for attendance:
 * 1. Backend local timestamp (often Asia/Kolkata wall time without Z)
 * 2. UTC / ISO timestamp converted by Date
 */
export function formatAttendanceTimestamp(
  localTimestamp?: string | null,
  utcTimestamp?: string | null,
): string {
  if (localTimestamp?.trim()) return formatTime(localTimestamp);
  if (utcTimestamp?.trim()) return formatTime(utcTimestamp);
  return 'Not available';
}

const SHORT_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

function formatDayMonthYearClock(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
): string | null {
  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    !Number.isInteger(day) ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31
  ) {
    return null;
  }
  const clock = formatClockFromParts(hour, minute);
  if (!clock) return null;
  return `${day} ${SHORT_MONTHS[month - 1]} ${year}, ${clock}`;
}

/**
 * Application/submission stamp for approval screens.
 * Uses the request creation timestamp (date and time), never a date-only
 * leave/attendance day.
 */
export function formatAppliedOn(
  value: string | null | undefined,
): string {
  if (!value?.trim()) return 'Not available';
  const trimmed = value.trim();
  if (DATE_ONLY.test(trimmed)) return 'Not available';

  const localMatch = trimmed.match(LOCAL_DATE_TIME);
  if (localMatch) {
    const [yearText, monthText, dayText] = localMatch[1].split('-');
    const [hourText, minuteText] = localMatch[2].split(':');
    const formatted = formatDayMonthYearClock(
      Number(yearText),
      Number(monthText),
      Number(dayText),
      Number(hourText),
      Number(minuteText),
    );
    if (formatted) return formatted;
  }

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return 'Not available';
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: BUSINESS_TIME_ZONE,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(parsed);
  const read = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find(part => part.type === type)?.value);
  const formatted = formatDayMonthYearClock(
    read('year'),
    read('month'),
    read('day'),
    read('hour'),
    read('minute'),
  );
  return formatted ?? 'Not available';
}

export function formatAttendanceDate(value: string | null | undefined): string {
  const dateOnly = calendarDateFromApi(value);
  if (!dateOnly) return 'Date unavailable';
  const date = new Date(`${dateOnly}T12:00:00`);
  if (Number.isNaN(date.getTime())) return 'Date unavailable';
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

export function formatAttendanceWeekday(value: string | null | undefined): string {
  const dateOnly = calendarDateFromApi(value);
  if (!dateOnly) return '';
  const date = new Date(`${dateOnly}T12:00:00`);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, { weekday: 'long' }).format(date);
}

/** Day Month Year without weekday, e.g. "24 May 2024". */
export function formatCalendarDate(value: string | null | undefined): string {
  const dateOnly = calendarDateFromApi(value);
  if (!dateOnly) return 'Date unavailable';
  const date = new Date(`${dateOnly}T12:00:00`);
  if (Number.isNaN(date.getTime())) return 'Date unavailable';
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

export function formatLeaveDate(value: string | null | undefined): string {
  return formatAttendanceDate(value);
}

export function formatLeaveApplicationDate(
  value: string | null | undefined,
): string {
  return formatAttendanceDate(value);
}

export function formatLeaveDuration(
  value: number | null | undefined,
  unit = 'DAYS',
): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return 'Not available';
  }
  const sign = value < 0 ? '-' : '';
  const amount = Math.abs(value);
  const normalizedUnit = unit.trim().toUpperCase();
  if (normalizedUnit === 'HOURS' || normalizedUnit === 'HOUR') {
    return amount === 1 ? `${sign}1 hour` : `${sign}${amount} hours`;
  }
  if (amount === 1) return `${sign}1 day`;
  if (amount > 0 && amount < 1) return `${sign}${amount} day`;
  if (amount === 0) return '0 days';
  return `${sign}${amount} days`;
}

export function formatWfhDate(value: string | null | undefined): string {
  return formatAttendanceDate(value);
}

export function formatWfhApplicationDate(
  value: string | null | undefined,
): string {
  return formatAttendanceDate(value);
}

export function formatWfhDuration(
  value: number | null | undefined,
  unit = 'DAYS',
): string {
  return formatLeaveDuration(value, unit);
}

export function formatPermissionDate(value: string | null | undefined): string {
  return formatAttendanceDate(value);
}

export function formatPermissionTime(value: string | null | undefined): string {
  return formatTime(value);
}

export function formatPermissionDuration(
  minutes: number | null | undefined,
): string {
  return formatMinutes(minutes);
}

/**
 * Display-only duration from same-day local times when backend omits minutes.
 * Not used for eligibility or submission.
 */
export function calculateDisplayDuration(
  fromTime: string | null | undefined,
  toTime: string | null | undefined,
): number | null {
  if (!fromTime || !toTime) return null;
  const fromMatch = fromTime.trim().match(/^(\d{2}):(\d{2})/);
  const toMatch = toTime.trim().match(/^(\d{2}):(\d{2})/);
  if (!fromMatch || !toMatch) return null;
  const fromMinutes = Number(fromMatch[1]) * 60 + Number(fromMatch[2]);
  const toMinutes = Number(toMatch[1]) * 60 + Number(toMatch[2]);
  if (
    !Number.isFinite(fromMinutes) ||
    !Number.isFinite(toMinutes) ||
    toMinutes <= fromMinutes
  ) {
    return null;
  }
  return toMinutes - fromMinutes;
}

export function getGreeting(date = new Date()): string {
  const hour = date.getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}
